// src/app/elements/calendar/EventEditModal.jsx
import React, { useCallback, useEffect, useState } from "react";
import "./EventEditModal.css";
import { API_BASE } from "../../../../../config/api";
import ConfirmDialog from "../../../../globalcontrols/confirmdialog/ConfirmDialog";

const STATUS_OPTIONS = ["Primer contacto","Cotización enviada","pre confirmado", "confirmado", "cancelado", "finalizado"];
const TYPE_OPTIONS = ["matrimonio","cumpleaños","baby shower","brunch","coffee break","corporativo","otro"];
const PAYMENT_STATUS = ["pendiente","pagado"];

// quick helpers
const pad = (n) => String(n).padStart(2, "0");
const toLocalDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
};
const toLocalTimeInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const mergeDateTimeLocal = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const [y,m,d] = dateStr.split("-").map(Number);
  let hh = 0, mm = 0;
  if (timeStr && timeStr.includes(":")) {
    const [H,M] = timeStr.split(":").map(Number);
    hh = H; mm = M;
  }
  const x = new Date();
  x.setFullYear(y, m-1, d);
  x.setHours(hh, mm, 0, 0);
  return x.toISOString();
};

// Display-only CLP formatting: $ and dot thousands (no decimals)
const formatCLP = (n) => {
  const num = Number(n) || 0;
  try {
    return `$ ${num.toLocaleString('es-CL')}`;
  } catch {
    // Fallback manual formatting
    return `$ ${num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  }
};

// Payment amount inputs: digits only, displayed with $ and dot thousands
const addThousands = (digits) => String(digits).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const normalizeMoneyInput = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  d = d.replace(/^0+(?=\d)/, ''); // remove leading zeros when followed by digits
  return d;
};
const formatMoneyDisplay = (digits) => digits ? `$ ${addThousands(digits)}` : '';

// Short date for UI: dd-mm-yyyy
const toDMY = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
};

export default function EventEditModal({ eventId, onClose, initialDate = null, createNew = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revision, setRevision] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  // Errors are tracked but not rendered inline (warnings use the modal below).
  const [, setError] = useState("");
  const [warnOpen, setWarnOpen] = useState(false);
  const [warnFields, setWarnFields] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // form state
  const [form, setForm] = useState({
    contact_name: "",
    contact_phone: "+56",
    contact_email: "",

    starts_at_date: "",
    starts_at_time: "",
    ends_at_date: "",
    ends_at_time: "",

    event_type: "otro",
    event_status: "pre confirmado",
    first_payment_status: "pendiente",
    first_payment_amount: 0,
    second_payment_status: "pendiente",
    second_payment_amount: 0,

    event_participants: 0,
    waiters_qty: 0,
    base_price_per_person: 0,
    drinks_price_per_person: 0,
    waiters_unitary_price: 0,
    other_amounts: [],

    event_products_list: [],
    purchase_list: [],

    id_uid: "",
    event_adress_street: "",
    event_adress_number: "",
    event_address_comune: "",
    event_address_apartment: "",
    confirmed_by: "",
    confirmed_at: null,
  });

  // --- Slider state / refs ---
  const [page, setPage] = useState(0); // 0..pagesCount-1
  const pagesCount = 4;
  const scrollRef = React.useRef(null);

  // Add-material mini modal state
  const [addOpen, setAddOpen] = useState(false);
  const [newMat, setNewMat] = useState({ description: "", quantity: 1 });
  // Add-other-amount mini modal state
  const [addOtherOpen, setAddOtherOpen] = useState(false);
  const [newOther, setNewOther] = useState({ item: "", amount: 0 });
  // Add-purchase mini modal state
  const [addPurchaseOpen, setAddPurchaseOpen] = useState(false);
  const [newPurchase, setNewPurchase] = useState({ item: "", qty: 1, unit_price: 0 });

  // Display state and helpers for payment amounts with $ and thousands
  const [firstPayDisplay, setFirstPayDisplay] = useState("");
  const [secondPayDisplay, setSecondPayDisplay] = useState("");

  // load event
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (!eventId || createNew) {
          const d = initialDate instanceof Date ? initialDate : null;
          const dateStr = d ? `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}` : "";
          setForm((f)=>({
            ...f,
            starts_at_date: dateStr,
            starts_at_time: "",
            ends_at_date:   dateStr, // prefill end date same as start date
            ends_at_time:   "",
            event_status: "Primer contacto",
          }));
          setFirstPayDisplay("");
          setSecondPayDisplay("");
          setError("");
          return;
        }
        const sessionData = JSON.parse(localStorage.getItem("sessionData"));
        if (!sessionData?.sessionKey || !sessionData?.username) {
          setError("Sesión inválida");
          setLoading(false);
          return;
        }

        const res = await fetch(`${API_BASE}/events/${eventId}`, {
          headers: {
            Authorization: sessionData.sessionKey,
            Username: sessionData.username,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ev = await res.json();
        setRevision(ev.revision);
        setConflict(false);

        // 👇 Debugging log
        console.log("Editing event loaded:", ev);

        setForm({
          contact_name: ev.contact_name || "",
          contact_phone: (ev.contact_phone ? (ev.contact_phone.startsWith("+56") ? ev.contact_phone : ("+56" + String(ev.contact_phone).replace(/\D/g, '').replace(/^56/, ''))) : "+56"),
          contact_email: ev.contact_email || "",

          starts_at_date: toLocalDateInput(ev.starts_at),
          starts_at_time: toLocalTimeInput(ev.starts_at),
          ends_at_date:   toLocalDateInput(ev.ends_at),
          ends_at_time:   toLocalTimeInput(ev.ends_at),

          event_type: ev.event_type || "otro",
          event_status: ev.event_status || "pre confirmado",
          first_payment_status: ev.first_payment_status || "pendiente",
          first_payment_amount: Number(ev.first_payment_amount || 0),
          second_payment_status: ev.second_payment_status || "pendiente",
          second_payment_amount: Number(ev.second_payment_amount || 0),

          event_participants: Number(ev.event_participants || 0),
          waiters_qty: Number(ev.waiters_qty || 0),
          base_price_per_person: Number(ev.base_price_per_person || 0),
          drinks_price_per_person: Number(ev.drinks_price_per_person || 0),
          waiters_unitary_price: Number(ev.waiters_unitary_price || 0),
          other_amounts: Array.isArray(ev.other_amounts) ? ev.other_amounts : [],

          event_products_list: Array.isArray(ev.event_products_list) ? ev.event_products_list : [],
          id_uid: ev.id_uid || "",
          purchase_list: Array.isArray(ev.purchase_list) ? ev.purchase_list : [],

          event_adress_street: ev.event_adress_street || "",
          event_adress_number: ev.event_adress_number || "",
          event_address_comune: ev.event_address_comune || "",
          event_address_apartment: ev.event_address_apartment || "",
          confirmed_by: ev.confirmed_by || "",
          confirmed_at: ev.confirmed_at || null,
        });
        // Initialize display strings for amounts
        try {
          const a1 = Number(ev.first_payment_amount || 0);
          const a2 = Number(ev.second_payment_amount || 0);
          setFirstPayDisplay(a1 > 0 ? formatMoneyDisplay(a1) : "");
          setSecondPayDisplay(a2 > 0 ? formatMoneyDisplay(a2) : "");
        } catch {}
        setError("");
      } catch (e) {
        console.error(e);
        setError("No se pudo cargar el evento.");
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, createNew, initialDate, reloadVersion]);

  const goToPage = useCallback((nextPage, { direction } = { direction: "right" }) => {
    const p = ((nextPage % pagesCount) + pagesCount) % pagesCount;
    if (scrollRef.current) {
      if (direction === "right") {
        scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      }
    }
    setPage(p);
  }, [pagesCount]);
  const goRight = useCallback(() => goToPage(page + 1, { direction: "right" }), [goToPage, page]);
  const goLeft = useCallback(() => goToPage(page - 1, { direction: "left" }), [goToPage, page]);

  // keyboard support: slide pages with arrow keys when modal open
  useEffect(() => {
    const onKey = (e) => {
      // Do not navigate when a mini modal is open
      if (addOpen) return;
      // Ignore if typing in form fields
      const tag = (e.target?.tagName || "").toLowerCase();
      const isEditable = tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable;
      if (isEditable) return;
      if (e.key === "ArrowRight") { e.preventDefault(); goRight(); }
      if (e.key === "ArrowLeft")  { e.preventDefault(); goLeft(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goLeft, goRight, addOpen, addOtherOpen, addPurchaseOpen]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Phone helpers: enforce +56 prefix and 9 digits after
  const normalizePhone = (raw = "") => {
    const s = String(raw || "");
    let t = s.replace(/[^\d+]/g, "");
    if (t.startsWith("+")) t = t.slice(1);
    if (t.startsWith("56")) t = t.slice(2);
    t = t.replace(/\D/g, "").slice(0, 9);
    return "+56" + t;
  };
  const handlePhoneChange = (e) => {
    const val = normalizePhone(e.target.value);
    setForm((f) => ({ ...f, contact_phone: val }));
  };

  const openAddMaterial = () => {
    setNewMat({ description: "", quantity: 1 });
    setAddOpen(true);
  };
  const closeAddMaterial = () => setAddOpen(false);
  const confirmAddMaterial = () => {
    const desc = String(newMat.description || "").trim();
    const qty = Number(newMat.quantity || 0);
    if (!desc) return; // simple guard
    const item = { item: desc, quantity: qty > 0 ? qty : 0 };
    setForm((f) => ({
      ...f,
      event_products_list: Array.isArray(f.event_products_list)
        ? [...f.event_products_list, item]
        : [item],
    }));
    setAddOpen(false);
    // scroll materials into view on add
    try {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    } catch {}
  };

  const removeMaterialAt = (idx) => {
    setForm((f) => ({
      ...f,
      event_products_list: (Array.isArray(f.event_products_list) ? f.event_products_list : []).filter((_, i) => i !== idx),
    }));
  };

  const confirmLocalRemove = (title, message, onRemove) => {
    setConfirmDialog({
      title,
      message,
      confirmLabel: "Eliminar",
      onConfirm: () => {
        onRemove();
        setConfirmDialog(null);
      },
    });
  };

  const openAddOther = () => {
    setNewOther({ item: "", amount: 0 });
    setAddOtherOpen(true);
  };
  const closeAddOther = () => setAddOtherOpen(false);
  const confirmAddOther = () => {
    const item = String(newOther.item || "").trim();
    const amt = Number(newOther.amount || 0);
    if (!item) return;
    const entry = { item, amount: isNaN(amt) ? 0 : amt };
    setForm((f) => ({
      ...f,
      other_amounts: Array.isArray(f.other_amounts) ? [...f.other_amounts, entry] : [entry],
    }));
    setAddOtherOpen(false);
  };

  const removeOtherAt = (idx) => {
    setForm((f) => ({
      ...f,
      other_amounts: (Array.isArray(f.other_amounts) ? f.other_amounts : []).filter((_, i) => i !== idx),
    }));
  };

  const removePurchaseAt = (idx) => {
    setForm((f) => ({
      ...f,
      purchase_list: (Array.isArray(f.purchase_list) ? f.purchase_list : []).filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");

      // Validate required fields (show warning modal instead of inline text)
      const missing = [];
      if (!String(form.contact_name || "").trim()) missing.push("Nombre");
      const phoneOk = /^\+56\d{9}$/.test(String(form.contact_phone || ""));
      if (!phoneOk) missing.push("Teléfono (formato +569XXXXXXXX)");
      if (!String(form.event_type || "").trim()) missing.push("Tipo de Evento");
      if (!String(form.starts_at_date || "").trim()) missing.push("Fecha inicio");
      if (!String(form.ends_at_date || "").trim()) missing.push("Fecha término");

      if (missing.length) {
        setWarnFields(missing);
        setWarnOpen(true);
        setSaving(false);
        return;
      }

      // arrays stored as arrays in state
      const materialsParsed = Array.isArray(form.event_products_list) ? form.event_products_list : [];
      const purchaseParsed = Array.isArray(form.purchase_list) ? form.purchase_list : [];

      const payload = {
        ...((eventId && !createNew) ? { revision } : {}),
        contact_name: form.contact_name,
        contact_phone: form.contact_phone,
        contact_email: form.contact_email,

        starts_at: mergeDateTimeLocal(form.starts_at_date, form.starts_at_time),
        ends_at:   mergeDateTimeLocal(form.ends_at_date, form.ends_at_time),

        event_type: form.event_type,
        event_status: form.event_status,
        first_payment_status: form.first_payment_status,
        first_payment_amount: Number(form.first_payment_amount || 0),
        second_payment_status: form.second_payment_status,
        second_payment_amount: Number(form.second_payment_amount || 0),

        event_participants: Number(form.event_participants || 0),
        waiters_qty: Number(form.waiters_qty || 0),
        base_price_per_person: Number(form.base_price_per_person || 0),
        drinks_price_per_person: Number(form.drinks_price_per_person || 0),
        waiters_unitary_price: Number(form.waiters_unitary_price || 0),
        other_amounts: Array.isArray(form.other_amounts) ? form.other_amounts : [],

        event_products_list: materialsParsed,
        purchase_list: purchaseParsed,

        event_adress_street: form.event_adress_street,
        event_adress_number: form.event_adress_number,
        event_address_comune: form.event_address_comune,
        event_address_apartment: form.event_address_apartment,
      };

      const sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!sessionData?.sessionKey || !sessionData?.username) {
        setError("Sesión inválida");
        setSaving(false);
        return;
      }

      const endpoint = (!eventId || createNew) ? `${API_BASE}/events` : `${API_BASE}/events/${eventId}`;
      const method = (!eventId || createNew) ? "POST" : "PUT";
      const res = await fetch(endpoint, {
        method,
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (data?.error === 'stale_event') setConflict(true);
        // A locked day comes back as a code plus a readable reason; show the reason.
        const msg = (data?.message) || data?.error || "No se pudo guardar el evento.";
        const labels = {
          contact_name: "Nombre",
          contact_phone: "Teléfono",
          event_type: "Tipo de Evento",
          starts_at: "Fecha inicio",
          ends_at: "Fecha término",
          event_adress_street: "Calle",
          event_adress_number: "Número",
          event_address_comune: "Comuna",
        };
        if (msg.startsWith("Missing required field:")) {
          const key = msg.split(":").slice(1).join(":").trim();
          setWarnFields([labels[key] || key]);
          setWarnOpen(true);
        } else {
          setWarnFields([msg]);
          setWarnOpen(true);
        }
        setSaving(false);
        return;
      }

      // success — close and signal “updated=true”
      onClose?.(true);
    } catch (e) {
      console.error(e);
      setError("Error de red al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="events-modal-backdrop" role="presentation" onClick={() => onClose?.(false)}>
      <div
        className="event-modal-edit-window"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {conflict && <div role="alert">
          El evento cambió. Tu borrador sigue abierto. Copia los cambios que quieras conservar antes de recargar.
          <button type="button" onClick={() => { setWarnOpen(false); setReloadVersion((value) => value + 1); }}>Descartar borrador y recargar</button>
        </div>}
        <div className="event-modal-edit-header">
          <div className="event-modal-edit-header-left" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="event-modal-edit-title" style={{ marginBottom: 4, fontWeight: 700 }}>
              {createNew ? "Crear nuevo evento" : `Editar ${form.event_type} de ${form.contact_name}`}
            </div>
            {String(form.event_status) === 'confirmado' && (
              <div style={{
                marginTop: 2,
                fontSize: 12,
                color: '#888'
              }}>
                Confirmado por {form.confirmed_by ? <b style={{ color: '#888' }}>{form.confirmed_by}</b> : '—'}
                {form.confirmed_at ? <> el {toDMY(form.confirmed_at)}</> : ''}
              </div>
            )}
          </div>
          <div className="event-modal-edit-header-right">
            {!createNew && form.id_uid ? (
              <span className="event-id-uid" title="Identificador del evento">#{form.id_uid}</span>
            ) : null}
            <button className="events-modal-close" onClick={() => onClose?.(false)} aria-label="Cerrar">×</button>
          </div>
        </div>
  
        {loading ? (
          <div className="events-modal-status">Cargando…</div>
        ) : (
          <>
            {/* Inline error text intentionally suppressed; warnings show in modal */}
            
            <div className="event-modal-edit-body">
              {/* Pager left arrow */}
              <div className="eem-arrow-placeholder">
                <button className="eem-arrow eem-left"  type="button" onClick={goLeft}  aria-label="Página anterior">‹</button>
              </div>
              {/* Slider viewport with vertical scroll */}
              <div className="eem-viewport" ref={scrollRef}>
                <div
                  className="eem-track"
                  style={{
                    width: `${pagesCount * 100}%`,
                    transform: `translateX(-${page * (100 / pagesCount)}%)`,
                  }}
                >
                  {/* PAGE 1 */}
                  <section className="eem-page">
              <div className="event-modal-edit-form">
                      {/* Basic info */}
                      <div className ="event-modal-edit-form-field-group">
                        <div className="event-modal-edit-form-field-group-title">Información básica</div>
                        <div className="event-modal-edit-form-field-group-content">
                          <div className="eem-grid-2">
                            <div className="event-modal-edit-form-field-container">
                              <label>Tipo de Evento</label>
                              <select value={form.event_type} onChange={(e)=>update("event_type", e.target.value)}>
                                {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Estado de Evento</label>
                              <select
                                value={form.event_status}
                                onChange={(e)=>{
                                  const v = e.target.value;
                                  setForm((f)=>{
                                    const next = { ...f, event_status: v };
                                    if (v === 'confirmado') {
                                      try {
                                        const sessionData = JSON.parse(localStorage.getItem('sessionData')) || {};
                                        if (!next.confirmed_by) next.confirmed_by = sessionData?.username || '';
                                      } catch {}
                                      if (!next.confirmed_at) next.confirmed_at = new Date().toISOString();
                                    }
                                    return next;
                                  });
                                }}
                              >
                                {(form.confirmed_at || form.event_status === 'confirmado'
                                  ? STATUS_OPTIONS.filter(s => !["Primer contacto","Cotización enviada","pre confirmado"].includes(s))
                                  : STATUS_OPTIONS
                                ).map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Contact */}
                      <div className="event-modal-edit-form-field-group">
                        <div className="event-modal-edit-form-field-group-title">Datos de Contacto</div>
                        <div className="event-modal-edit-form-field-group-content">
                          <div className="eem-grid-2">
                            <div className="event-modal-edit-form-field-container">
                              <label>Nombre</label>
                              <input value={form.contact_name} onChange={(e)=>update("contact_name", e.target.value)} />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Teléfono</label>
                              <input value={form.contact_phone} onChange={handlePhoneChange} placeholder="+569XXXXXXXX" />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Email</label>
                              <input value={form.contact_email} onChange={(e)=>update("contact_email", e.target.value)} />
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Date/time */}
                      <div className="event-modal-edit-form-field-group">
                        <div className="event-modal-edit-form-field-group-title">Fecha y Hora</div>
                        <div className="event-modal-edit-form-field-group-content">
                          <div className="eem-grid-2">
                            <div className="event-modal-edit-form-field-container">
                              <label>Fecha inicio</label>
                              <input type="date" value={form.starts_at_date} onChange={(e)=>update("starts_at_date", e.target.value)} />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Hora inicio</label>
                              <input type="time" value={form.starts_at_time} onChange={(e)=>update("starts_at_time", e.target.value)} />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Fecha término</label>
                              <input type="date" value={form.ends_at_date} onChange={(e)=>update("ends_at_date", e.target.value)} />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Hora término</label>
                              <input type="time" value={form.ends_at_time} onChange={(e)=>update("ends_at_time", e.target.value)} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                  
                  {/* PAGE 2 */}
                  <section className="eem-page">
                    <div className="event-modal-edit-form">
                      <div className="event-modal-edit-form-field-group">
                        <div className="event-modal-edit-form-field-group-title">Ubicación</div>
                        <div className="event-modal-edit-form-field-group-content">
                          <div className="eem-grid-2">
                            <div className="event-modal-edit-form-field-container">
                              <label>Calle</label>
                              <input value={form.event_adress_street} onChange={(e)=>update("event_adress_street", e.target.value)} />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Número</label>
                              <input value={form.event_adress_number} onChange={(e)=>update("event_adress_number", e.target.value)} />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Comuna</label>
                              <input value={form.event_address_comune} onChange={(e)=>update("event_address_comune", e.target.value)} />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Depto.</label>
                              <input value={form.event_address_apartment} onChange={(e)=>update("event_address_apartment", e.target.value)} />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className ="event-modal-edit-form-field-group">
                        <div className="event-modal-edit-form-field-group-title">Estatus de Pagos</div>
                        <div className="event-modal-edit-form-field-group-content">
                          <div className="eem-grid-2">
                            <div className="event-modal-edit-form-field-container">
                              <label>1er pago</label>
                              <select value={form.first_payment_status} onChange={(e)=>update("first_payment_status", e.target.value)}>
                                {PAYMENT_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                              <div style={{ marginTop: 6 }}>
                                <label>Monto 1er pago</label>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={firstPayDisplay}
                                  onChange={(e)=>{
                                    const digits = normalizeMoneyInput(e.target.value);
                                    setFirstPayDisplay(formatMoneyDisplay(digits));
                                    update("first_payment_amount", Number(digits || 0));
                                  }}
                                  style={{ width: '100%' }}
                                  placeholder="$ 0"
                                />
                              </div>
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>2do pago</label>
                              <select value={form.second_payment_status} onChange={(e)=>update("second_payment_status", e.target.value)}>
                                {PAYMENT_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                              <div style={{ marginTop: 6 }}>
                                <label>Monto 2do pago</label>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={secondPayDisplay}
                                  onChange={(e)=>{
                                    const digits = normalizeMoneyInput(e.target.value);
                                    setSecondPayDisplay(formatMoneyDisplay(digits));
                                    update("second_payment_amount", Number(digits || 0));
                                  }}
                                  style={{ width: '100%' }}
                                  placeholder="$ 0"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className ="event-modal-edit-form-field-group">
                        <div className="event-modal-edit-form-field-group-title">Asistentes y Costos</div>
                        <div className="event-modal-edit-form-field-group-content">
                          <div className="eem-grid-2">
                            <div className="event-modal-edit-form-field-container">
                              <label>Participantes</label>
                              <input type="number" min="0" value={form.event_participants} onChange={(e)=>update("event_participants", e.target.value)} />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Precio bebidas / persona</label>
                              <input type="number" min="0" value={form.drinks_price_per_person} onChange={(e)=>update("drinks_price_per_person", e.target.value)} />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className ="event-modal-edit-form-field-group">
                        <div className="event-modal-edit-form-field-group-title">Garzones y Costos</div>
                        <div className="event-modal-edit-form-field-group-content">
                          <div className="eem-grid-2">
                            <div className="event-modal-edit-form-field-container">
                              <label>Garzones</label>
                              <input type="number" min="0" value={form.waiters_qty} onChange={(e)=>update("waiters_qty", e.target.value)} />
                            </div>
                            <div className="event-modal-edit-form-field-container">
                              <label>Precio unitario garzón</label>
                              <input type="number" min="0" value={form.waiters_unitary_price} onChange={(e)=>update("waiters_unitary_price", e.target.value)} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                  {/* PAGE 3 */}
                  <section className="eem-page">
                    <div className="event-modal-edit-form">
                      {/* Vertical split: top = Otros Montos, bottom = Materiales */}
                      <div className="eem-page3-vertical">
                        {/* Otros Montos (top half) */}
                        <div className="eem-section">
                          <div className ="event-modal-edit-form-field-group" style={{ height: "100%" }}>
                            <div className="event-modal-edit-form-field-group-title">
                              <span>Otros Montos</span>
                              <button
                                type="button"
                                className="eem-round-btn"
                                aria-label="Agregar otro monto"
                                onClick={openAddOther}
                              >
                                +
                              </button>
                            </div>
                            <div className="event-modal-edit-form-field-group-content" style={{ width: "100%" }}>
                              <div className="eem-table-container">
                                <div className="eem-materials-table-wrapper" style={{ width: "100%", flex: 1, minHeight: 0, height: 'auto' }}>
                                  {(!form.other_amounts || form.other_amounts.length === 0) ? (
                                    <div className="eem-materials-empty">Sin otros montos</div>
                                  ) : (
                                    <table className="eem-materials-table">
                                      <thead>
                                    <tr>
                                      <th>Ítem</th>
                                      <th>Monto</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {form.other_amounts.map((it, idx) => {
                                      const item = (it && (it.item ?? it.descripcion ?? it.desc ?? it.name)) ?? "-";
                                      const amt = Number(it && (it.amount ?? it.monto)) || 0;
                                      return (
                                        <tr key={idx}>
                                          <td>{String(item)}</td>
                                          <td className="eem-qty-cell">{formatCLP(amt)}</td>
                                          <td className="eem-actions-cell">
                                            <button
                                              type="button"
                                              className="eem-round-btn eem-minus"
                                              aria-label={`Eliminar ${item}`}
                                              onClick={() => confirmLocalRemove("Eliminar monto", `Eliminar "${item}" de otros montos?`, () => removeOtherAt(idx))}
                                            >
                                              −
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                    </tbody>
                                  </table>
                                )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Materiales (bottom half) */}
                        <div className="eem-section">
                          <div className ="event-modal-edit-form-field-group" style={{ height: "100%" }}>
                            <div className="event-modal-edit-form-field-group-title">
                              <span>Materiales</span>
                              <button
                                type="button"
                                className="eem-round-btn"
                                aria-label="Agregar material"
                                onClick={openAddMaterial}
                              >
                                +
                              </button>
                            </div>
                            <div className="event-modal-edit-form-field-group-content" style={{ width: "100%" }}>
                              <div className="eem-table-container">
                                <div className="eem-materials-table-wrapper" style={{ width: "100%", flex: 1, minHeight: 0, height: 'auto' }}>
                                {(!form.event_products_list || form.event_products_list.length === 0) ? (
                                  <div className="eem-materials-empty">Sin materiales</div>
                                ) : (
                                  <table className="eem-materials-table">
                                    <thead>
                                    <tr>
                                      <th>Descripción</th>
                                      <th>Cantidad</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                        {form.event_products_list.map((it, idx) => {
                                          const desc = (typeof it === "string")
                                            ? it
                                            : (it && (it.item ?? it.description ?? it.desc ?? it.name)) ?? "-";
                                          const rawQty = (it && typeof it === "object" && (it.quantity ?? it.qty ?? it.cantidad)) ?? 0;
                                          const qty = Number(rawQty) || 0;
                                          return (
                                            <tr key={idx}>
                                              <td>{String(desc)}</td>
                                              <td className="eem-qty-cell">{qty}</td>
                                              <td className="eem-actions-cell">
                                                <button
                                                  type="button"
                                                  className="eem-round-btn eem-minus"
                                                  aria-label={`Eliminar ${desc}`}
                                                  onClick={() => confirmLocalRemove("Eliminar material", `Eliminar "${desc}" de los materiales?`, () => removeMaterialAt(idx))}
                                                >
                                                  −
                                                </button>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </table>
                                )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                  {/* PAGE 4 - Lista de Compras */}
                  <section className="eem-page">
                    <div className="event-modal-edit-form">
                      <div className ="event-modal-edit-form-field-group" style={{ height: "100%", width: "100%" }}>
                        <div className="event-modal-edit-form-field-group-title">
                          <span>Lista de Compras</span>
                          <button
                            type="button"
                            className="eem-round-btn"
                            aria-label="Agregar compra"
                            onClick={() => setAddPurchaseOpen(true)}
                          >
                            +
                          </button>
                        </div>
                        <div className="event-modal-edit-form-field-group-content" style={{ width: "100%" }}>
                          <div className="eem-materials-table-wrapper" style={{ width: "100%" }}>
                            {(!form.purchase_list || form.purchase_list.length === 0) ? (
                              <div className="eem-materials-empty">Sin compras</div>
                            ) : (
                              <>
                              <table className="eem-materials-table eem-purchase-table">
                                <thead>
                                  <tr>
                                    <th>Descripción</th>
                                    <th>Cantidad</th>
                                    <th>Precio unitario</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {form.purchase_list.map((it, idx) => {
                                    const desc = (typeof it === "string")
                                      ? it
                                      : (it && (it.item ?? it.description ?? it.desc ?? it.name)) ?? "-";
                                    const rawQty = (it && typeof it === "object" && (it.qty ?? it.quantity ?? it.cantidad)) ?? 0;
                                    const qty = Number(rawQty) || 0;
                                    const unit = Number(it && (it.unit_price ?? it.price ?? it.unitPrice ?? 0)) || 0;
                                    return (
                                      <tr key={idx}>
                                        <td>{String(desc)}</td>
                                        <td className="eem-qty-cell">{qty}</td>
                                        <td className="eem-qty-cell">{formatCLP(unit)}</td>
                                        <td className="eem-actions-cell">
                                          <button
                                            type="button"
                                            className="eem-round-btn eem-minus"
                                            aria-label={`Eliminar ${desc}`}
                                            onClick={() => confirmLocalRemove("Eliminar compra", `Eliminar "${desc}" de la lista de compras?`, () => removePurchaseAt(idx))}
                                          >
                                            −
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              <div className="eem-purchase-total-row">Total: {formatCLP((Array.isArray(form.purchase_list)?form.purchase_list:[]).reduce((sum, it)=>{
                                const q = Number(it && (it.qty ?? it.quantity ?? it.cantidad)) || 0;
                                const u = Number(it && (it.unit_price ?? it.price ?? it.unitPrice)) || 0;
                                return sum + (q*u);
                              },0))}</div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Inline mini modal moved outside sliding track */}
                </div>
              </div>
              {/* Pager right arrow */}
              <div className="eem-arrow-placeholder">
                <button className="eem-arrow eem-right" type="button" onClick={goRight} aria-label="Página siguiente">›</button>
              </div>
            </div>
            {/* Add-material modal overlay, outside of transformed/overflow containers */}
            {addOpen && (
              <div className="eem-mini-modal-backdrop" role="presentation" onClick={closeAddMaterial}>
                <div className="eem-mini-modal" role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()}>
                  <div className="eem-mini-modal-header">
                    <div>Agregar material</div>
                    <button className="events-modal-close" onClick={closeAddMaterial} aria-label="Cerrar">×</button>
                  </div>
                  <div className="eem-mini-modal-body">
                    <div className="eem-grid-2">
                      <div className="event-modal-edit-form-field-container" style={{ gridColumn: "1 / span 2" }}>
                        <label>Descripción</label>
                        <input
                          value={newMat.description}
                          onChange={(e)=>setNewMat((m)=>({ ...m, description: e.target.value }))}
                        />
                      </div>
                      <div className="event-modal-edit-form-field-container" style={{ gridColumn: "1 / span 2" }}>
                        <label>Cantidad</label>
                        <input
                          type="number"
                          min="0"
                          value={newMat.quantity}
                          onChange={(e)=>setNewMat((m)=>({ ...m, quantity: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="eem-mini-modal-footer">
                    <button className="event-editing-modal-button" onClick={closeAddMaterial}>Cancelar</button>
                    <button className="event-editing-modal-button" onClick={confirmAddMaterial}>Agregar</button>
                  </div>
                </div>
              </div>
            )}
            {/* Add-other-amount modal overlay */}
            {addOtherOpen && (
              <div className="eem-mini-modal-backdrop" role="presentation" onClick={closeAddOther}>
                <div className="eem-mini-modal" role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()}>
                  <div className="eem-mini-modal-header">
                    <div>Agregar otro monto</div>
                    <button className="events-modal-close" onClick={closeAddOther} aria-label="Cerrar">×</button>
                  </div>
                  <div className="eem-mini-modal-body">
                    <div className="eem-grid-2">
                      <div className="event-modal-edit-form-field-container" style={{ gridColumn: "1 / span 2" }}>
                        <label>Ítem</label>
                        <input value={newOther.item} onChange={(e)=>setNewOther((m)=>({ ...m, item: e.target.value }))} />
                      </div>
                      <div className="event-modal-edit-form-field-container" style={{ gridColumn: "1 / span 2" }}>
                        <label>Monto</label>
                        <input type="number" min="0" value={newOther.amount} onChange={(e)=>setNewOther((m)=>({ ...m, amount: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                  <div className="eem-mini-modal-footer">
                    <button className="event-editing-modal-button" onClick={closeAddOther}>Cancelar</button>
                    <button className="event-editing-modal-button" onClick={confirmAddOther}>Agregar</button>
                  </div>
                </div>
              </div>
            )}
            {/* Warning modal for missing fields */}
            {warnOpen && (
              <div className="eem-mini-modal-backdrop" role="presentation" onClick={()=>setWarnOpen(false)}>
                <div className="eem-mini-modal" role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()}>
                  <div className="eem-mini-modal-header">
                    <div>Atención</div>
                    <button className="events-modal-close" onClick={()=>setWarnOpen(false)} aria-label="Cerrar">×</button>
                  </div>
                  <div className="eem-mini-modal-body">
                    <div style={{ color: '#fff' }}>
                      Falta completar los campos:
                      <ul>
                        {warnFields.map((f, i) => (<li key={i}>{f}</li>))}
                      </ul>
                    </div>
                  </div>
                  <div className="eem-mini-modal-footer">
                    <button className="event-editing-modal-button" onClick={()=>setWarnOpen(false)}>Entendido</button>
                  </div>
                </div>
              </div>
            )}
            {/* Add-purchase modal overlay */}
            {addPurchaseOpen && (
              <div className="eem-mini-modal-backdrop" role="presentation" onClick={()=>setAddPurchaseOpen(false)}>
                <div className="eem-mini-modal" role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()}>
                  <div className="eem-mini-modal-header">
                    <div>Agregar compra</div>
                    <button className="events-modal-close" onClick={()=>setAddPurchaseOpen(false)} aria-label="Cerrar">×</button>
                  </div>
                  <div className="eem-mini-modal-body">
                    <div className="eem-grid-2">
                      <div className="event-modal-edit-form-field-container" style={{ gridColumn: "1 / span 2" }}>
                        <label>Descripción</label>
                        <input value={newPurchase?.item || ''} onChange={(e)=>setNewPurchase((m)=>({ ...(m||{}), item: e.target.value }))} />
                      </div>
                      <div className="event-modal-edit-form-field-container" style={{ gridColumn: "1 / span 2" }}>
                        <label>Cantidad</label>
                        <input type="number" min="0" value={newPurchase?.qty || 0} onChange={(e)=>setNewPurchase((m)=>({ ...(m||{}), qty: e.target.value }))} />
                      </div>
                      <div className="event-modal-edit-form-field-container" style={{ gridColumn: "1 / span 2" }}>
                        <label>Precio unitario</label>
                        <input type="number" min="0" value={newPurchase?.unit_price || 0} onChange={(e)=>setNewPurchase((m)=>({ ...(m||{}), unit_price: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                  <div className="eem-mini-modal-footer">
                    <button className="event-editing-modal-button" onClick={()=>setAddPurchaseOpen(false)}>Cancelar</button>
                    <button className="event-editing-modal-button" onClick={()=>{
                      const item = String(newPurchase?.item||'').trim();
                      const qty = Number(newPurchase?.qty||0);
                      const unit = Number(newPurchase?.unit_price||0);
                      if(!item) return; 
                      setForm((f)=>({ ...f, purchase_list: Array.isArray(f.purchase_list) ? [...f.purchase_list, { item, qty: isNaN(qty)?0:qty, unit_price: isNaN(unit)?0:unit }] : [{ item, qty: isNaN(qty)?0:qty, unit_price: isNaN(unit)?0:unit }] }));
                      setAddPurchaseOpen(false);
                    }}>Agregar</button>
                  </div>
                </div>
              </div>
            )}
            <ConfirmDialog
              open={Boolean(confirmDialog)}
              title={confirmDialog?.title}
              message={confirmDialog?.message}
              confirmLabel={confirmDialog?.confirmLabel}
              onConfirm={confirmDialog?.onConfirm}
              onCancel={() => setConfirmDialog(null)}
            />
            <div className="event-modal-edit-footer">
              <div className="event-modal-edit-footer-placeholder">
                <div className="eem-page-indicator">Página {page + 1} / {pagesCount}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="event-editing-modal-button" onClick={() => onClose?.(false)} disabled={saving}>Cancelar</button>
                  <button className="event-editing-modal-button" onClick={handleSave} disabled={saving}>
                    {saving ? "Guardando..." : "Guardar"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
